/* eslint-disable react/forbid-prop-types,no-param-reassign */
/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import validator from '@rjsf/validator-ajv8';
import { FoForm as Form } from '@rjsf/core';
import _isEqual from 'lodash/isEqual';
import { debounce } from 'lodash';

const delay = 500;

const FormWrapper = (props) => {
  const abortControllerChangeRef = React.useRef();
  const abortControllerPostProcessRef = React.useRef();
  let {onBlur} = props;
  let {onChange} = props;
  let onSectionChange = props.onSectionChange || (() => {});

  const delayedOnBlur = onBlur && debounce(onBlur, delay);
  onBlur = async (fnProps) => {
    if (delayedOnBlur) {
      if (abortControllerChangeRef.current) abortControllerChangeRef.current.abort();
      abortControllerChangeRef.current = new AbortController();
      delayedOnBlur(fnProps, abortControllerChangeRef.current);
    }
  };

  const delayedOnChange = onChange && debounce(onChange, delay);
  onChange = async (fnProps) => {
    if (delayedOnChange) {
      if (abortControllerChangeRef.current) abortControllerChangeRef.current.abort();
      abortControllerChangeRef.current = new AbortController();
      delayedOnChange(fnProps, abortControllerChangeRef.current);
    }
  };

  const delayedOnSectionChange = delayedOnChange && debounce(onSectionChange, delay);
  onSectionChange = async (fnProps) => {
    if (delayedOnSectionChange) {
      if (abortControllerPostProcessRef.current) abortControllerPostProcessRef.current.abort();
      abortControllerPostProcessRef.current = new AbortController();
      delayedOnSectionChange(fnProps, abortControllerPostProcessRef.current);
    }
  };

  return <Form {...props} onChange={onChange} onBlur={onBlur} onSectionChange={onSectionChange} />;
};

const FormMemo = React.memo(FormWrapper, (prev, next) => {
  if (!_isEqual(prev.readOnly, next.readOnly)) {
    return false;
  }
  if (!_isEqual(prev.uiSchema, next.uiSchema)) {
    return false;
  }
  if (!_isEqual(prev.formData, next.formData)) {
    return false;
  }
  if (!_isEqual(prev.extraErrors, next.extraErrors)) {
    return false;
  }
  if (!_isEqual(prev.errors, next.errors)) {
    return false;
  }
  return true;
});

function withTheme(themeProps) {
  return forwardRef(({ fields, widgets, ...directProps }, ref) => {
    fields = { ...themeProps.fields, ...fields };
    widgets = { ...themeProps.widgets, ...widgets };
    return (
      <FormMemo
        validator={validator} {...themeProps} {...directProps} fields={fields} widgets={widgets}
        ref={ref}
      />
    );
  });
}

withTheme.propTypes = {
  widgets: PropTypes.object,
  fields: PropTypes.object,
};

export default withTheme;
